import { communicationBlockByStepId, toOptionalIso } from './launchStepSources';

import type {
    LaunchConfigSnapshot,
    LaunchEmailSnapshot,
    LaunchStepSnapshot,
} from './launchConfigSnapshot';
import type { CommunicationBlockDefinition } from './launchStepSources';
import type { EvaluationTypeValue } from '@/constants/cycle-evaluation/CycleEvaluation';
import type {
    CycleLaunchCommunicationSnapshot,
    CycleLaunchEvaluationSnapshot,
    CycleLaunchSnapshot,
} from '@/types/cycle-evaluation/CycleLaunchSnapshot';

import {
    CycleCommunicationRecipientType,
    CycleCommunicationMilestoneType,
} from '@/constants/cycle-evaluation/CycleCommunication';
import { CycleAudienceType } from '@/constants/cycle-evaluation/CycleEvaluation';
import {
    CommunicationBlockPersistenceRoute,
    isLaunchCommunicationBlock,
} from '@/pages/CycleEvaluation/Communications/communicationBlocks';
import { LAUNCH_MILESTONE_BY_TYPE } from '@/pages/CycleEvaluation/Deadlines/milestones';

interface BuildBaselineSnapshotParams {
    launchedConfiguration: CycleLaunchSnapshot;
    templateLabelById: Map<string, string>;
    unknownTemplateLabel: string;
}

const toCriteriaLabels = (snapshot: CycleLaunchSnapshot): string[] =>
    (snapshot.audience?.cycleAudienceCriterias ?? []).map(
        (criteria) =>
            `${criteria.criteriaType}:${criteria.customAttributeId ?? ''}:${criteria.value}`,
    );

const toLegacyEmail = ({
    isEnabled,
    title,
    body,
}: {
    isEnabled?: boolean | null;
    title?: Record<string, string> | null;
    body?: Record<string, string> | null;
}): LaunchEmailSnapshot => ({
    isEnabled: isEnabled ?? true,
    titleByLocale: title ?? {},
    bodyByLocale: body ?? {},
});

const toBlockEmail = ({
    block,
    recipient,
}: {
    block: CycleLaunchCommunicationSnapshot | undefined;
    recipient: (typeof CycleCommunicationRecipientType)[keyof typeof CycleCommunicationRecipientType];
}): LaunchEmailSnapshot | undefined => {
    const email = block?.emails.find((candidate) => candidate.recipient === recipient);

    if (!block || !email) {
        return undefined;
    }

    return {
        isEnabled: block.isEnabled && email.isEnabled,
        titleByLocale: { ...email.title },
        bodyByLocale: { ...email.body },
    };
};

const readStepEmails = ({
    definition,
    evaluation,
    block,
}: {
    definition: CommunicationBlockDefinition;
    evaluation: CycleLaunchEvaluationSnapshot | undefined;
    block: CycleLaunchCommunicationSnapshot | undefined;
}) => {
    if (definition.persistenceRoute === CommunicationBlockPersistenceRoute.LEGACY_SCHEDULE) {
        return {
            employeeEmail: toLegacyEmail({
                isEnabled: evaluation?.isEmployeeEmailEnabled,
                title: evaluation?.employeeEmailTitle,
                body: evaluation?.employeeEmailMessage,
            }),
            managerEmail: toLegacyEmail({
                isEnabled: evaluation?.isManagerEmailEnabled,
                title: evaluation?.managerEmailTitle,
                body: evaluation?.managerEmailMessage,
            }),
        };
    }

    return {
        employeeEmail: definition.hasEmployeeCard
            ? toBlockEmail({ block, recipient: CycleCommunicationRecipientType.EMPLOYEE })
            : undefined,
        managerEmail: toBlockEmail({ block, recipient: CycleCommunicationRecipientType.MANAGER }),
    };
};

const buildBaselineSnapshot = ({
    launchedConfiguration,
    templateLabelById,
    unknownTemplateLabel,
}: BuildBaselineSnapshotParams): LaunchConfigSnapshot => {
    const evaluationByType = new Map<EvaluationTypeValue, CycleLaunchEvaluationSnapshot>(
        launchedConfiguration.evaluations.map((evaluation) => [evaluation.reviewType, evaluation]),
    );
    const milestoneByType = new Map(
        launchedConfiguration.milestones.map((milestone) => [milestone.milestoneType, milestone]),
    );
    const communicationByType = new Map(
        launchedConfiguration.communications.map((block) => [block.milestoneType, block]),
    );

    const steps: LaunchStepSnapshot[] = [];

    communicationBlockByStepId.forEach((definition) => {
        const evaluation = isLaunchCommunicationBlock(definition)
            ? evaluationByType.get(definition.evaluationType)
            : undefined;

        if (isLaunchCommunicationBlock(definition) && !evaluation) {
            return;
        }

        const isLaunchStep = isLaunchCommunicationBlock(definition);
        const communicationType = isLaunchStep
            ? CycleCommunicationMilestoneType.MANAGER_ASSESSMENT_LAUNCH
            : definition.milestoneType;
        const scheduledAt = isLaunchStep
            ? toOptionalIso(evaluation?.scheduledAt)
            : toOptionalIso(milestoneByType.get(definition.milestoneType)?.scheduledAt);

        steps.push({
            id: definition.id,
            labelKey: definition.labelKey,
            scheduledAt,
            ...readStepEmails({
                definition,
                evaluation,
                block: communicationByType.get(communicationType),
            }),
        });
    });

    const evaluationsByType = Object.fromEntries(
        [...evaluationByType.entries()].map(([reviewType, evaluation]) => [
            reviewType,
            {
                labelKey: LAUNCH_MILESTONE_BY_TYPE[reviewType].labelKey,
                isSelected: true,
                templateId: evaluation.reviewTemplateId ?? undefined,
                templateLabel: evaluation.reviewTemplateId
                    ? (templateLabelById.get(evaluation.reviewTemplateId) ?? unknownTemplateLabel)
                    : '',
                scheduledAt: toOptionalIso(evaluation.scheduledAt),
            },
        ]),
    );

    return {
        name: launchedConfiguration.name,
        cycleStart: toOptionalIso(launchedConfiguration.evaluationPeriodStart),
        cycleEnd: toOptionalIso(launchedConfiguration.evaluationPeriodEnd),
        audience: {
            isEntireCompany:
                launchedConfiguration.audience?.cycleAudienceType ===
                CycleAudienceType.ENTIRE_COMPANY,
            criteriaLabels: toCriteriaLabels(launchedConfiguration),
        },
        evaluationsByType,
        steps,
    };
};

export { buildBaselineSnapshot };
